import {addAlert} from "../containers/App/actions";
import request from "superagent";
import Compress from "compress.js";
import EXIF from "exif-js";
import JSZip from 'jszip';
import moment from "moment-timezone";
import {findEXIFinHEIC} from "../utilities/heic-exif";
import {UPLOAD_FORMAT_DATE_ONLY, UPLOAD_FORMAT_FULL, UPLOAD_FORMAT_TIME_ONLY} from "../constants";
import config from "../config/environment";
import {heicTo} from "heic-to";

const getImageTypeFromHeader = (data) => {
    const bytes = new Uint8Array(data);

    if (bytes.length >= 8 &&
        bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
        bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) {
        return 'image/png';
    }

    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        return 'image/jpeg';
    }

    if (bytes.length >= 6 &&
        bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38 &&
        (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61) {
        return 'image/gif';
    }

    if (bytes.length >= 12 &&
        bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
        bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
        return 'image/webp';
    }

    if (bytes.length >= 16 &&
        bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
        const view = new DataView(
            bytes.buffer,
            bytes.byteOffset,
            bytes.byteLength,
        );

        const boxSize = Math.min(
            view.getUint32(0, false),
            bytes.length,
        );

        const heicBrands = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs'];
        const getBrand = (offset) => String.fromCharCode(...bytes.slice(offset, offset + 4));

        if (heicBrands.includes(getBrand(8))) {
            return 'image/heic';
        }

        for (let offset = 16; offset + 4 <= boxSize; offset += 4) {
            if (heicBrands.includes(getBrand(offset))) {
                return 'image/heic';
            }
        }
    }

    return null;
};

const withDetectedImageType = (file, type) => {
    const typedFile = new Blob([file], {type});
    typedFile.name = file.name;
    return typedFile;
};

const detectImageType = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(getImageTypeFromHeader(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file.slice(0, 4100));
});

const convertHeic = (file, callback) => heicTo({
    blob: file,
    type: "image/jpeg",
    quality: 1,
})
    .then(callback)
    .catch((error) => {
        console.error("HEIC conversion failed", error);
    });

export const dataUpload = (url, acceptedFiles, callback, customAlert, data, showSuccessMessage = true) => async (dispatch, getState) => {
        const targetFile = acceptedFiles.constructor === Array || acceptedFiles.constructor === FileList
            ? acceptedFiles[0]
            : acceptedFiles;

        const detectedType = targetFile instanceof Blob ? await detectImageType(targetFile) : null;

        showSuccessMessage && dispatch(
            addAlert({
                message: "Uploading. Please wait...",
                mood: "warning",
                dismissAfter: 3000,
            }));

        function doUpload(file) {
            let req = request
                .post(`${config.apiUrl}${url}`)
                .set("Authorization", getState().auth.token)

            if (data) {
                req.field(data)
            }
            req.attach("file", file);

            req.then(
                response => {
                    showSuccessMessage && dispatch(
                        addAlert({
                            message: customAlert ? customAlert(response) : "Uploaded",
                            mood: "success",
                            dismissAfter: 1500,
                        })
                    );
                    callback && callback(response.body);
                },
                error => {
                    if (error instanceof TypeError) {
                        dispatch(addAlert({message: error.toString()}));
                        console.error('Network error or CORS issue:', error);
                    } else if (error.name === 'AbortError') {
                        dispatch(addAlert({message: error.toString()}));
                        console.error('Request was aborted:', error);
                    } else if (!error.status) {
                        console.log('Request was aborted:', error);
                    } else {
                        dispatch(addAlert({message: error.toString()}));
                        console.error("Upload failed", error);
                    }
                }
            )
        }

        if (detectedType === 'image/heic') {
            convertHeic(targetFile, (result) => {
                result.name = targetFile.name;
                compressAndUpload(result, 1)
            })
        } else if (detectedType) {
            compressAndUpload(withDetectedImageType(targetFile, detectedType), 1);
        } else if (targetFile.type.includes("video/webm")) {
            if (!targetFile.name) {
                targetFile.name = "issue_video.webm"
            }
            doUpload(targetFile)
        } else if (targetFile.type.includes('image/')) {
            const compress = new Compress();
            compress.compress([targetFile], {
                size: 4,
                quality: .75,
                maxWidth: 1920,
                maxHeight: 1920,
                resize: true,
            }).then((result) => {
                const compressed = result[0];
                const file = Compress.convertBase64ToFile(compressed.data, compressed.ext);
                file.name = targetFile.name;
                doUpload(file);
            });
        } else if (targetFile.name && targetFile.name.includes('.key')) {
            let zip = new JSZip();
            zip.file(`${targetFile.name}`, targetFile);
            zip.generateAsync({type: "blob"})
                .then((file) => {
                    file.name = targetFile.name
                    doUpload(file)
                });
        } else {
            doUpload(targetFile);
        }

        function compressAndUpload(targetFile, orientation) {
            if (targetFile.type.includes('image/')) {
                const compress = new Compress();
                compress.compress([targetFile], {
                    size: 4,
                    quality: .75,
                    maxWidth: 1920,
                    maxHeight: 1920,
                    resize: true
                }).then((result) => {
                    const compressed = result[0];
                    const file = Compress.convertBase64ToFile(compressed.data, compressed.ext);
                    file.name = targetFile.name;

                    if (!file.name) {
                        file.name = acceptedFiles[0].name
                    }
                    doUpload(file, orientation);
                });
            } else {
                doUpload(targetFile);
            }
        }
    }
;


export const handleUpload = (url, acceptedFiles, callback, customAlert, fileName = null, override_name = false, clientTimezone, timestampType, asHidden = false, isAsset = false, errCallback = () => null) => async (dispatch, getState) => {
    const sourceFile = typeof acceptedFiles !== 'string' ? acceptedFiles[0] : null;
    const detectedSourceType = sourceFile instanceof Blob ? await detectImageType(sourceFile) : null;

    if (detectedSourceType && detectedSourceType !== sourceFile.type) {
        const typedFile = withDetectedImageType(sourceFile, detectedSourceType);
        acceptedFiles = [typedFile];
    }

    const noDate = timestampType === "no_date";
    const dateOnly = timestampType === "created_at_d" || timestampType === "taken_at_d";
    const timeOnly = timestampType === "created_at_t" || timestampType === "taken_at_t";

    console.time("### upload time")

    dispatch(
        addAlert({
            message: "Uploading. Please wait...",
            mood: "warning",
            dismissAfter: 3000,
        }));

    function doUpload(file, orientation, takenAtDateFormatted) {
        let orient = orientation ? orientation : 1
        let req;
        let takenAtTimestampZoned = moment(takenAtDateFormatted).format();
        let uploadedAtTimestampZoned = moment().format();

        if (clientTimezone) {
            takenAtTimestampZoned = moment(takenAtDateFormatted).tz(clientTimezone).format();
            uploadedAtTimestampZoned = moment().tz(clientTimezone).format();
        }

        let saveFormat = UPLOAD_FORMAT_FULL;

        if (timeOnly && !noDate) {
            saveFormat = UPLOAD_FORMAT_TIME_ONLY;
        }

        if (dateOnly && !noDate) {
            saveFormat = UPLOAD_FORMAT_DATE_ONLY;
        }

        if ((takenAtTimestampZoned || uploadedAtTimestampZoned) && isAsset) {
            if (takenAtTimestampZoned === "Invalid date" || takenAtDateFormatted === null) {
                dispatch(
                    addAlert({
                        message: "Uploaded image has no Taken At date",
                        mood: "warning",
                        dismissAfter: 3000,
                    }));
            }
            req = request
                .post(`${config.apiUrl}${url}`)
                .field('orientation', orient)
                .field('timestamp_taken_at', takenAtTimestampZoned)
                .field('timestamp_uploaded_at', uploadedAtTimestampZoned)
                .field('saveFormat', saveFormat)
                .field('timestamp_type', timestampType || "")
                .field('as_hidden', asHidden)
                .set("Authorization", getState().auth.token);
        } else {
            req = request
                .post(`${config.apiUrl}${url}`)
                .field('orientation', orient)
                .field('as_hidden', asHidden)
                .set("Authorization", getState().auth.token);
        }

        req.attach("file", file);
        req.then(
            response => {
                dispatch(
                    addAlert({
                        message: customAlert ? customAlert(response) : "Uploaded",
                        mood: "success",
                        dismissAfter: 1500,
                    })
                );
                console.timeEnd("### upload time")
                callback && callback(response.body);
            },
            error => {
                errCallback && errCallback(error)
                dispatch(addAlert({message: error.toString()}));
                console.error("Upload failed", error);
            }
        )
    }

    if (acceptedFiles[0].type === 'application/pdf') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/pdf'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (typeof acceptedFiles === 'string') {
        let base64 = acceptedFiles.split(';base64,')[1]
        let ext = acceptedFiles.substring("data:image/".length, acceptedFiles.indexOf(";base64"))
        let type = acceptedFiles.substring("data:".length, acceptedFiles.indexOf(";base64"))
        const file = Compress.convertBase64ToFile(base64, type);
        file.name = `new_name.${ext}`

        if (!file.name) {
            file.name = acceptedFiles[0].name
        }
        doUpload(file);
    } else if (detectedSourceType === 'image/heic') {
        const reader = new FileReader();
        reader.onload = function () {
            let takenAtDateFormatted = null;
            try {
                const tags = findEXIFinHEIC(reader.result)
                const takenAtDate = tags?.DateTime;
                const inputFormat = 'YYYY:MM:DD HH:mm:ss';
                const outputFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
                takenAtDateFormatted = takenAtDate ? moment(takenAtDate, inputFormat).format(outputFormat) : null;
            } catch (e) {
                console.log(e)
            }
            convertHeic(acceptedFiles[0], (result) => {
                compressAndUpload(result, 1, takenAtDateFormatted)
            })
        }
        reader.readAsArrayBuffer(acceptedFiles[0]);
    } else if (acceptedFiles[0].type === 'text/csv') {
        const blob = new Blob([acceptedFiles[0]], {type: 'text/csv'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'application/msword') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/msword'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'application/vnd.ms-excel') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/vnd.ms-excel'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'text/plain') {
        const blob = new Blob([acceptedFiles[0]], {type: 'text/plain'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'application/x-iwork-pages-sffpages') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/x-iwork-pages-sffpages'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === 'application/x-iwork-numbers-sffnumbers') {
        const blob = new Blob([acceptedFiles[0]], {type: 'application/x-iwork-numbers-sffnumbers'})
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else if (acceptedFiles[0].type === '') {
        const blob = new Blob([acceptedFiles[0]])
        blob.name = acceptedFiles[0]?.name ? acceptedFiles[0].name : fileName
        doUpload(blob)
    } else {
        EXIF.getData(acceptedFiles[0], function () {
            const orientation = EXIF.getTag(this, "Orientation") || 1;
            const takenAtDate = EXIF.getTag(this, "DateTime");
            const inputFormat = 'YYYY:MM:DD HH:mm:ss';
            const outputFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
            const takenAtDateFormatted = moment(takenAtDate, inputFormat).format(outputFormat);

            compressAndUpload(acceptedFiles[0], orientation, takenAtDateFormatted);
        })
    }

    function compressAndUpload(targetFile, orientation, takenAtDateFormatted) {
        if (targetFile.type.includes('image/')) {
            const isPng = targetFile.type === 'image/png';
            const prepare = isPng ? pngToWhiteBackground(targetFile) : Promise.resolve(targetFile);

            prepare.then((processedFile) => {
                const compress = new Compress();
                compress.compress([processedFile], {
                    size: 4,
                    quality: .75,
                    maxWidth: 1920,
                    maxHeight: 1920,
                    resize: true
                }).then((result) => {
                    const compressed = result[0];
                    const file = Compress.convertBase64ToFile(compressed.data, compressed.ext);
                    file.name = targetFile.name;

                    if (!file.name) {
                        file.name = acceptedFiles[0].name
                    }
                    if (override_name) {
                        file.name = fileName
                    }
                    doUpload(file, orientation, takenAtDateFormatted);
                });
            });
        } else {
            doUpload(targetFile, null, takenAtDateFormatted);
        }
    }
};

function pngToWhiteBackground(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');

                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                ctx.drawImage(img, 0, 0);

                canvas.toBlob((blob) => {
                    const newFile = new File([blob], file.name.replace(/\.png$/i, '.jpg'), {type: 'image/jpeg'});
                    resolve(newFile);
                }, 'image/jpeg', 0.95);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}
